import React from 'react';
import { Button, Dropdown, Form, TextArea } from 'semantic-ui-react';

/**
 * Inline editor for one part of a Services cell, always fitting the cell's current width.
 * - mode 'people': the picker starts EMPTY so you can type the replacement right away. Whoever you pick
 *   replaces the current people; picking nobody (or removing your picks) leaves the cell as it was.
 *   "Unassign" clears the cell.
 * - mode 'notes': just the notes box (Scripture Reading passage, Preaching topic).
 * Changes apply as you make them; Esc, "Done" or a click outside the cell closes it.
 */
export default class ServiceCellEditor extends React.Component {
	constructor(props) {
		super(props);
		this.rootRef = React.createRef();
		this.dropdownRef = React.createRef();
		this.state = {
			/** people picked in this session (they replace what was there) */
			picks: [],
			// the box keeps exactly what was typed (incl. a trailing space); the page only stores real changes
			notes: props.value?.notes ?? '',
		};
		/** people when the editor opened: restored if all picks are removed again */
		this.initialIds = [...(props.value?.memberIds ?? [])];
	}

	componentDidMount = () => {
		this.focusTimer = setTimeout(() => {
			if (this.props.mode === 'notes') {
				const el = this.rootRef.current?.querySelector('textarea');
				el?.focus();
				el?.setSelectionRange(el.value.length, el.value.length);
			}
			else this.dropdownRef.current?.searchRef?.current?.focus();
		}, 0);
		document.addEventListener('keydown', this.onKeyDown, true);
		document.addEventListener('mousedown', this.onDocumentMouseDown, true);
	}

	componentWillUnmount = () => {
		clearTimeout(this.focusTimer);
		document.removeEventListener('keydown', this.onKeyDown, true);
		document.removeEventListener('mousedown', this.onDocumentMouseDown, true);
	}

	onKeyDown = (e) => {
		// notes can be multi-line, so plain Enter is a new line there; Cmd/Ctrl+Enter finishes
		const done = e.key === 'Escape' || (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && this.props.mode === 'notes');
		if (!done) return;
		e.preventDefault();
		e.stopPropagation();
		this.props.onClose();
	}

	onDocumentMouseDown = (e) => {
		// the whole cell (incl. its padding) counts as inside: the page marks it with .svc-cell-editing
		const cell = this.rootRef.current?.closest('.svc-cell-editing') ?? this.rootRef.current;
		if (cell && !cell.contains(e.target)) this.props.onClose();
	}

	setPicks = (picks) => {
		this.setState({ picks });
		this.props.onChange({ memberIds: picks.length > 0 ? picks : this.initialIds });
	}

	unassign = () => {
		this.props.onChange({ memberIds: [] });
		this.props.onClose();
	}

	setNotes = (notes) => {
		this.setState({ notes });
		// differing notes on linked fields are only replaced by real text, never wiped by an empty box
		const keepMixed = this.props.cell?.notesMixed && !(notes ?? '').trim();
		this.props.onChange({ notes: keepMixed ? undefined : notes });
	}

	render() {
		const { mode, options, search, notesPlaceholder, currentText } = this.props;
		const { picks, notes } = this.state;

		return (
			<div ref={this.rootRef} className='svc-cell-editor' onClick={(e) => e.stopPropagation()}>
				<Form size='small'>
					{mode === 'notes'
						? <TextArea
							rows={2}
							placeholder={notesPlaceholder ?? 'Notes'}
							value={notes}
							onChange={(e, { value: text }) => this.setNotes(text)}
						/>
						: <Dropdown
							ref={this.dropdownRef}
							fluid multiple selection
							openOnFocus
							placeholder={currentText ? `Replace ${currentText}…` : 'Type a name…'}
							options={options}
							search={search ?? true}
							value={picks}
							onChange={(e, { value: memberIds }) => this.setPicks(memberIds)}
						/>
					}
				</Form>
				<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, marginTop: 4 }}>
					{mode !== 'notes' && this.initialIds.length > 0 && picks.length === 0 &&
						<Button type='button' size='mini' compact basic content='Unassign' onClick={this.unassign} />
					}
					<Button type='button' size='mini' compact basic content='Done' onClick={this.props.onClose} />
				</div>
			</div>
		);
	}
}
